/**
 * Die Geräteliste der Karten „Vespera Desktop (Mac)“ und „Chrome-Extension“ — zeichnen und bedienen. (07.10.2026,
 * Plan „mehrere Geräte“, S4 für die Macs, S6 für die Browser: DIESELBE Liste mit dem Parameter `art`)
 *
 *   AUTOMATISCH
 *   Mehrere Macs sind bereit. …
 *   ● MacBook Pro                         Vespera arbeitet hier
 *     verbunden seit 17:33 · gerade benutzt
 *     Bei dir: 1.0.9 · neueste Testversion: 1.0.9
 *     [Umbenennen] [Trennen]
 *   ○ Mac mini                            bereit
 *     …
 *
 * Die Sätze stehen in frontend/lib/mac-geraete.ts, das Aussehen in style.css (`.mac-…`, keine Inline-Styles — die
 * Klassen heißen seit S4 so und tragen beide Listen; die Browser-Liste hat dazu `data-art="browser"`).
 *
 * Bedienung:
 *  - „Hier arbeiten“, „Wieder automatisch“: eine Anfrage, die Knöpfe der Zeile sind solange gesperrt.
 *  - „Umbenennen“: das Namensfeld IN der Zeile (Enter sichert, Escape bricht ab); dieselbe Namensregel wie der Server.
 *  - „Trennen“: Rückfrage IN der Seite — der erste Tipp fragt „Wirklich trennen?“, erst der zweite trennt; Abbrechen,
 *    Escape oder 20 s ohne Tipp nehmen es zurück. Kein confirm().
 *  - Nach jeder Aktion zeigt die Liste die Antwort des Servers; Fehler stehen in Worten an der Zeile.
 *  - „Jetzt aktualisieren“ (nur Macs, 12.10.2026): unter der Versionszeile, wenn der Mac eine neuere freigegebene Fassung kennt und
 *    die App es kann; Knopf und Satz stehen in frontend/lib/update-starten.ts. Der Satz zeigt den Zustand an Ort und Stelle (kein
 *    Neuaufbau der Zeile), darum steht `updateStarten` nicht in der Signatur.
 *
 * ⚠️ Der Stand kommt alle 10 s neu (settings-view.ts). Die Zeile, die gerade bedient wird (Feld offen, Rückfrage),
 *    bleibt dabei stehen — sonst verschwände das Feld unter den Fingern; alle anderen Zeilen zeichnen sich neu.
 *    Solange eine Anfrage läuft, wartet das Neuzeichnen ganz (die Antwort zeichnet ohnehin).
 */
import {
  geraetAktion,
  GeraetAktionFehler,
  type BrowserGeraet,
  type DaemonStatus,
  type GeraeteArt,
  type MacAktion,
  type MacGeraet,
  type MacWahl,
} from "./connectors-api.js";
import {
  AKTION_WORT,
  aktionFehlerText,
  bereinigterName,
  browserAlsGeraet,
  erfolgText,
  geraetArt,
  geraetDetail,
  geraetHinweise,
  geraetVersion,
  inNfc,
  NAME_REGEL,
  geraetWort,
  trennenFrage,
  wahlZeile,
  zeilenAktionen,
  zustandWort,
  type Geraet,
  type ZeilenAktion,
} from "./mac-geraete.js";
import { versionZeile } from "./version-zeile.js";
import { baueUpdateSteuerung } from "./update-starten.js";
import type { UpdateStandAntwort } from "../../shared/update-starten.js";

/** Was die Liste vom Stand braucht: die Geräte, die Wahl und die Versionsangaben für die Zeile je Gerät. */
type ListenStand = Pick<DaemonStatus, "aktuell" | "download" | "downloadVersion"> & {
  geraete: Geraet[];
  wahl: MacWahl | null;
  /** 12.10.2026: der Stand von „Jetzt aktualisieren“ (nur Macs; fehlt bei einem älteren Server oder bei Browsern). */
  updateStarten?: UpdateStandAntwort | null;
};

interface MacGeraeteListe {
  element: HTMLElement;
  /**
   * Die Meldung nach einer Aktion (role=status). Steht AUSSERHALB von `element` — die Karte setzt sie darunter: wird
   * der letzte Mac getrennt, verschwindet die Liste ganz, die Meldung muss stehen bleiben (Prüfung 07.10.). Ohne
   * Knopf in der Liste fällt der Fokus auf sie.
   */
  meldung: HTMLElement;
  /**
   * Den Stand zeichnen. `abgefragtUm` (performance.now): wann die Abfrage dafür losging — ein Stand, der vor dem
   * Ende der letzten Aktion abgefragt wurde, ist älter als deren Antwort und wird übergangen (sonst spränge die Liste
   * kurz zurück).
   */
  zeichne: (s: ListenStand, abgefragtUm?: number) => void;
}

interface MacListeOptionen {
  /** Nach einer Aktion: den ganzen Stand neu holen (Kopfzeichen, alte Felder, „Weiteren Mac verbinden“). */
  nachher: () => void;
}

interface GeraeteListeOptionen extends MacListeOptionen {
  /** Macs (POST /api/daemon/geraet) oder Browser (POST /api/ext/geraet). */
  art: GeraeteArt;
}

/** Die Antwort einer Aktion in die Form der Liste: Macs kommen schon so, Browser werden umgeformt. */
const EINGANG: Readonly<Record<GeraeteArt, (roh: unknown[]) => Geraet[]>> = {
  mac: (roh) => roh as MacGeraet[],
  browser: (roh) => (roh as BrowserGeraet[]).map(browserAlsGeraet),
};

/** So lange bleibt eine Meldung stehen, wenn sich der Stand danach ändert — dann könnte sie nicht mehr stimmen. */
const MELDUNG_GILT_MS = 15_000;
/** So lange wartet „Wirklich trennen?“ auf den zweiten Tipp. */
const RUECKFRAGE_MS = 20_000;
/** Ein zweiter Tipp früher als das zählt nicht (zweimal Enter im Abstand von 80 ms trennte sofort, Prüfung 07.10.). */
const ZWEITER_TIPP_MS = 400;

let zaehler = 0;
const neueId = (praefix: string, was: string): string => `${praefix}-${was}-${++zaehler}`;

/** Die Liste der Macs (Karte „Vespera Desktop (Mac)“). */
export function baueMacGeraeteListe(o: MacListeOptionen): MacGeraeteListe {
  return baueGeraeteListe({ ...o, art: "mac" });
}

/** Die Liste einer Art — Macs oder Browser; alle Sätze kommen mit `art` aus mac-geraete.ts. */
export function baueGeraeteListe(o: GeraeteListeOptionen): MacGeraeteListe {
  const { art } = o;
  const praefix = neueId(art === "mac" ? "mac-geraete" : "browser-geraete", "liste");
  const element = document.createElement("div");
  element.className = "mac-geraete";
  element.dataset.art = art;

  const wahlBox = document.createElement("div");
  wahlBox.className = "mac-wahl";
  wahlBox.hidden = true;
  const wahlText = document.createElement("div");
  wahlText.className = "mac-wahl-text";
  const wahlTitel = document.createElement("span");
  wahlTitel.className = "mac-wahl-titel";
  const wahlSatz = document.createElement("p");
  wahlSatz.className = "mac-wahl-satz";
  wahlText.append(wahlTitel, wahlSatz);
  const zurueck = document.createElement("button");
  zurueck.type = "button";
  zurueck.className = "mac-knopf";
  zurueck.dataset.aktion = "automatik";
  zurueck.textContent = "Wieder automatisch";
  wahlBox.append(wahlText, zurueck);

  const liste = document.createElement("ul");
  liste.className = "mac-geraete-liste";
  liste.setAttribute("aria-label", art === "mac" ? "Deine Macs" : "Deine Browser");

  // Bleibt stehen (nie neu gebaut), damit ein Vorleseprogramm jede neue Meldung hört.
  const meldung = document.createElement("p");
  meldung.className = "mac-geraete-meldung";
  meldung.setAttribute("role", "status");
  meldung.tabIndex = -1;

  element.append(wahlBox, liste);

  // Nur bei Macs: das Ein-Klick-Update. Der Browser-Liste fehlt es (die Erweiterung aktualisiert man von Hand).
  const update =
    art === "mac"
      ? baueUpdateSteuerung({ anker: element, nachher: o.nachher, neuZeichnen: () => neuZeichnen() })
      : null;

  let stand: ListenStand | null = null;
  let gezeichnet = "";
  /** Die eine Zeile, die gerade bedient wird — sie bleibt beim Neuzeichnen stehen. */
  let offen: { id: string; art: "umbenennen" | "trennen" } | null = null;
  let laeuft = false;
  /** Ein Fehler an einer Zeile — bleibt beim Neuzeichnen stehen, bis zur nächsten Aktion (oder bis er alt ist). */
  let zeilenFehler: { id: string; text: string; um: number } | null = null;
  let meldungUm = 0;
  /** Die Uhr der offenen Rückfrage beim Trennen — und seit wann sie fragt. Eine Rückfrage zur Zeit. */
  let rueckfrageUhr: number | null = null;
  let rueckfrageSeit = 0;
  const rueckfrageUhrLoeschen = (): void => {
    if (rueckfrageUhr !== null) window.clearTimeout(rueckfrageUhr);
    rueckfrageUhr = null;
  };
  /** Wann die letzte Antwort einer Aktion kam (performance.now — feiner als Millisekunden, sonst könnte ein Abruf
   *  aus derselben Millisekunde als „nachher“ oder „vorher“ durchgehen). */
  let aktionFertigUm = 0;

  const melde = (text: string, art: "ok" | "fehler"): void => {
    meldung.dataset.art = art;
    meldung.textContent = text;
    meldungUm = Date.now();
  };

  /** Eine neue Bedienung beginnt: alte Meldungen (an jeder Zeile und unter der Liste) gelten nicht mehr. */
  const meldungenLeeren = (): void => {
    zeilenFehler = null;
    for (const m of [meldung, ...liste.querySelectorAll<HTMLElement>(":scope > li > .mac-geraet-meldung")]) {
      m.textContent = "";
      delete m.dataset.art;
    }
  };

  /** Ein Text in die Meldezeile einer Zeile (die schon steht, damit er angesagt wird). */
  const zeilenMeldung = (zeileEl: Element | null, text: string, art: "fehler" | "frage"): void => {
    const m = zeileEl?.querySelector<HTMLElement>(":scope > .mac-geraet-meldung");
    if (!m) return;
    m.dataset.art = art;
    m.textContent = text;
  };

  /** Fokus nach dem Neuzeichnen wiederfinden: dieselbe Zeile, derselbe Knopf — sonst der erste der Zeile. */
  const fokusMerken = (): { id: string | null; aktion: string | null } | null => {
    const a = document.activeElement;
    if (!(a instanceof HTMLElement) || !element.contains(a)) return null;
    return { id: a.closest<HTMLElement>("[data-geraet-id]")?.dataset.geraetId ?? null, aktion: a.dataset.aktion ?? null };
  };
  const fokusSetzen = (ziel: { id: string | null; aktion: string | null } | null): void => {
    if (!ziel) return;
    // Steht der Fokus noch sichtbar in der Liste (in der geschonten Zeile) oder auf der Meldung, bleibt er dort.
    const a = document.activeElement;
    if (a instanceof HTMLElement && (element.contains(a) || a === meldung) && !a.closest("[hidden]")) return;
    const zeile = ziel.id ? zeileVon(ziel.id) : null;
    const knopf =
      (zeile && ziel.aktion ? zeile.querySelector<HTMLElement>(`button[data-aktion="${ziel.aktion}"]`) : null) ??
      zeile?.querySelector<HTMLElement>("button") ??
      (ziel.aktion === "automatik" && !wahlBox.hidden && !zurueck.hidden ? zurueck : null) ??
      liste.querySelector<HTMLElement>("button") ??
      // Kein Knopf mehr (das letzte Gerät ist getrennt): die Meldung, die sagt, was geschah.
      (meldung.textContent ? meldung : null);
    knopf?.focus();
  };
  const zeileVon = (id: string): HTMLLIElement | null =>
    [...liste.querySelectorAll<HTMLLIElement>(":scope > li")].find((z) => z.dataset.geraetId === id) ?? null;

  const neuZeichnen = (fokus = fokusMerken()): void => {
    if (!stand) return;
    const s = stand;
    const w = wahlZeile(s.geraete, s.wahl, art);
    wahlBox.hidden = !w;
    if (w) {
      wahlBox.dataset.modus = w.zurueck ? "hand" : "automatisch";
      wahlTitel.textContent = w.titel;
      wahlSatz.textContent = w.satz;
      zurueck.hidden = !w.zurueck;
    }
    if (offen && !s.geraete.some((g) => g.id === offen?.id)) offen = null;
    const schonen = offen ? zeileVon(offen.id) : null;
    const soll = s.geraete.map((g) => (schonen && g.id === offen?.id ? schonen : zeile(g, s)));
    // Die geschonte Zeile wird nie herausgenommen oder verschoben (sonst verlöre ihr Feld den Fokus): erst alles
    // andere weg, dann die neuen Zeilen davor und dahinter einfügen.
    for (const alt of [...liste.children]) if (alt !== schonen) alt.remove();
    const k = schonen ? soll.indexOf(schonen) : -1;
    soll.forEach((li, i) => {
      if (li === schonen) return;
      if (schonen && i < k) liste.insertBefore(li, schonen);
      else liste.appendChild(li);
    });
    liste.hidden = s.geraete.length === 0;
    gezeichnet = signatur(s);
    fokusSetzen(fokus);
  };

  const zeile = (g: Geraet, s: ListenStand): HTMLLIElement => {
    const li = document.createElement("li");
    li.className = "mac-geraet";
    li.dataset.geraetId = g.id;
    li.dataset.art = geraetArt(g);
    const nameId = neueId(praefix, "name");

    const kopf = document.createElement("div");
    kopf.className = "mac-geraet-kopf";
    const punkt = document.createElement("span");
    punkt.className = "mac-geraet-punkt";
    punkt.setAttribute("aria-hidden", "true");
    const name = document.createElement("span");
    name.className = "mac-geraet-name";
    name.id = nameId;
    name.textContent = g.name;
    const zustand = document.createElement("span");
    zustand.className = "mac-geraet-zustand";
    zustand.textContent = zustandWort(g);
    kopf.append(punkt, name, zustand);
    li.appendChild(kopf);

    const detail = geraetDetail(g);
    if (detail) {
      const p = document.createElement("p");
      p.className = "mac-geraet-detail";
      p.textContent = detail;
      li.appendChild(p);
    }
    const version = geraetVersion(g, s, art);
    if (version) li.appendChild(versionZeile(null, version));
    if (update) for (const teil of update.teile(g, s, nameId, neueId(praefix, "update"))) li.appendChild(teil);
    const { notizen, warnung } = geraetHinweise(g);
    for (const n of notizen) {
      const p = document.createElement("p");
      p.className = "mac-geraet-notiz";
      p.textContent = n;
      li.appendChild(p);
    }
    if (warnung) {
      const warn = document.createElement("p");
      warn.className = "connector-warn";
      warn.textContent = warnung;
      li.appendChild(warn);
    }

    // Die Frage beim Trennen und Fehler an dieser Zeile — leer ohne Inhalt, aber immer da (role=status).
    const frage = document.createElement("p");
    frage.className = "mac-geraet-meldung";
    frage.id = neueId(praefix, "frage");
    frage.setAttribute("role", "status");
    if (zeilenFehler?.id === g.id) {
      frage.dataset.art = "fehler";
      frage.textContent = zeilenFehler.text;
    }

    const aktionen = document.createElement("div");
    aktionen.className = "mac-geraet-aktionen";
    for (const a of zeilenAktionen(g)) aktionen.appendChild(knopf(a, g, nameId, li));
    li.append(frage, aktionen);
    return li;
  };

  const knopf = (a: ZeilenAktion, g: Geraet, nameId: string, li: HTMLLIElement): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = a === "waehlen" ? "mac-knopf mac-knopf-haupt" : "mac-knopf";
    b.dataset.aktion = a;
    b.textContent = AKTION_WORT[a];
    // Der Name des Geräts als Beschreibung: „Umbenennen“ allein sagt einem Vorleseprogramm nicht, welches.
    b.setAttribute("aria-describedby", nameId);
    if (a === "waehlen") b.addEventListener("click", () => void ausfuehren("waehlen", g, b));
    if (a === "umbenennen") b.addEventListener("click", () => umbenennenOeffnen(g, li));
    if (a === "trennen") b.addEventListener("click", () => trennenTippen(g, b, li));
    return b;
  };

  /** Die Zeile wieder so zeichnen, wie der Stand sie zeigt (Rückfrage oder Feld zu). */
  const schliessen = (id: string, aktion: string): void => {
    rueckfrageUhrLoeschen();
    offen = null;
    zeilenFehler = null;
    const alt = zeileVon(id);
    const g = stand?.geraete.find((x) => x.id === id);
    if (alt && g && stand) alt.replaceWith(zeile(g, stand));
    zeileVon(id)?.querySelector<HTMLElement>(`button[data-aktion="${aktion}"]`)?.focus();
  };

  /** Erster Tipp: fragen. Zweiter Tipp: trennen. Abbrechen, Escape oder 20 s ohne Tipp: zurück. */
  const trennenTippen = (g: Geraet, b: HTMLButtonElement, li: HTMLLIElement): void => {
    if (laeuft) return;
    if (b.dataset.sicher === "ja") {
      if (Date.now() - rueckfrageSeit < ZWEITER_TIPP_MS) return;
      void ausfuehren("trennen", g, b);
      return;
    }
    if (offen) schliessen(offen.id, offen.art);
    meldungenLeeren();
    offen = { id: g.id, art: "trennen" };
    b.dataset.sicher = "ja";
    b.textContent = "Wirklich trennen?";
    // Während der Rückfrage zählen nur zwei Knöpfe: „Wirklich trennen?“ und „Abbrechen“.
    for (const k of b.parentElement?.querySelectorAll<HTMLElement>("button") ?? []) if (k !== b) k.hidden = true;
    zeilenMeldung(li, trennenFrage(g, art), "frage");
    const frage = li.querySelector<HTMLElement>(":scope > .mac-geraet-meldung");
    if (frage) b.setAttribute("aria-describedby", `${b.getAttribute("aria-describedby") ?? ""} ${frage.id}`.trim());
    const abbrechen = document.createElement("button");
    abbrechen.type = "button";
    abbrechen.className = "mac-knopf";
    abbrechen.dataset.aktion = "abbrechen";
    abbrechen.textContent = "Abbrechen";
    const zuruecknehmen = (): void => {
      if (offen?.id === g.id && offen.art === "trennen" && !laeuft) schliessen(g.id, "trennen");
    };
    abbrechen.addEventListener("click", zuruecknehmen);
    for (const k of [b, abbrechen]) {
      k.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          zuruecknehmen();
        }
      });
    }
    b.parentElement?.appendChild(abbrechen);
    // Vergessen: nach 20 s ist es wieder „Trennen“ — eine alte Rückfrage soll niemand aus Versehen bestätigen.
    // ⚠️ Die Uhr gehört genau zu DIESER Rückfrage: Abbrechen, Bestätigen und ein Fehler löschen sie (eine alte Uhr
    //    schloss nach einem Fehler sonst die nächste Rückfrage zu früh — Prüfung 07.10.).
    rueckfrageUhrLoeschen();
    rueckfrageSeit = Date.now();
    rueckfrageUhr = window.setTimeout(() => {
      rueckfrageUhr = null;
      if (b.isConnected && b.dataset.sicher === "ja") zuruecknehmen();
    }, RUECKFRAGE_MS);
  };

  /** Das Namensfeld in der Zeile. */
  const umbenennenOeffnen = (g: Geraet, li: HTMLLIElement): void => {
    if (laeuft) return;
    if (offen) schliessen(offen.id, offen.art);
    const zeileJetzt = zeileVon(g.id) ?? li;
    meldungenLeeren();
    offen = { id: g.id, art: "umbenennen" };
    const frage = zeileJetzt.querySelector<HTMLElement>(":scope > .mac-geraet-meldung");
    const form = document.createElement("form");
    form.className = "mac-geraet-umbenennen";
    form.noValidate = true;
    const feldId = neueId(praefix, "feld");
    const label = document.createElement("label");
    label.className = "mac-geraet-feld-titel";
    label.htmlFor = feldId;
    label.textContent = `Neuer Name für ${g.name}`;
    const feld = document.createElement("input");
    feld.className = "mac-geraet-feld";
    feld.id = feldId;
    feld.type = "text";
    feld.value = g.name;
    feld.maxLength = 40;
    feld.autocomplete = "off";
    feld.spellcheck = false;
    if (frage) feld.setAttribute("aria-describedby", frage.id);
    const knoepfe = document.createElement("div");
    knoepfe.className = "mac-geraet-aktionen";
    const sichern = document.createElement("button");
    sichern.type = "submit";
    sichern.className = "mac-knopf mac-knopf-haupt";
    sichern.dataset.aktion = "sichern";
    sichern.textContent = "Sichern";
    const abbrechen = document.createElement("button");
    abbrechen.type = "button";
    abbrechen.className = "mac-knopf";
    abbrechen.dataset.aktion = "abbrechen";
    abbrechen.textContent = "Abbrechen";
    knoepfe.append(sichern, abbrechen);
    form.append(label, feld, knoepfe);

    const zuruecknehmen = (): void => {
      if (!laeuft) schliessen(g.id, "umbenennen");
    };
    abbrechen.addEventListener("click", zuruecknehmen);
    form.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        zuruecknehmen();
      }
    });
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      // Nichts geändert: zu, ohne Prüfung — der alte Name darf Zeichen tragen, die die Regel nicht kennt.
      if (feld.value.trim() === g.name) {
        zuruecknehmen();
        return;
      }
      const neu = bereinigterName(feld.value);
      if (!neu) {
        zeilenMeldung(zeileJetzt, NAME_REGEL, "fehler");
        feld.setAttribute("aria-invalid", "true");
        feld.focus();
        return;
      }
      if (neu === g.name) {
        zuruecknehmen();
        return;
      }
      void ausfuehren("umbenennen", g, sichern, neu, feld);
    });

    // Kopf und Knöpfe der Zeile weichen dem Feld (es nennt den Mac); Verbindung, Version und Hinweise bleiben
    // darunter stehen. Die Meldezeile rückt direkt unter das Feld, damit ein Fehler dort steht, wo getippt wird.
    const kopf = zeileJetzt.querySelector<HTMLElement>(":scope > .mac-geraet-kopf");
    if (kopf) kopf.hidden = true;
    zeileJetzt.querySelector(":scope > .mac-geraet-aktionen")?.remove();
    if (kopf) kopf.after(form);
    else zeileJetzt.prepend(form);
    if (frage) {
      frage.textContent = "";
      delete frage.dataset.art;
      form.after(frage);
    }
    feld.focus();
    feld.select();
  };

  const ausfuehren = async (
    aktion: MacAktion,
    g: Geraet | null,
    b: HTMLButtonElement,
    name?: string,
    feld?: HTMLInputElement,
  ): Promise<void> => {
    if (laeuft) return;
    laeuft = true;
    rueckfrageUhrLoeschen();
    // Beim Umbenennen bleibt der Fehler am Feld stehen, bis die Antwort kommt; sonst beginnt alles neu.
    if (aktion !== "umbenennen") meldungenLeeren();
    else {
      zeilenFehler = null;
      meldung.textContent = "";
      delete meldung.dataset.art;
    }
    const zeileEl = g ? zeileVon(g.id) : null;
    const gesperrt = [...(zeileEl ?? wahlBox).querySelectorAll<HTMLButtonElement | HTMLInputElement>("button, input")];
    for (const k of gesperrt) k.disabled = true;
    b.setAttribute("aria-busy", "true");
    try {
      const antwort = await geraetAktion(art, aktion, g?.id, name);
      const neu = inNfc({ geraete: EINGANG[art](antwort.geraete), wahl: antwort.wahl });
      laeuft = false;
      aktionFertigUm = performance.now();
      offen = null;
      if (stand) stand = { ...stand, geraete: neu.geraete, wahl: neu.wahl };
      // Die bediente Zeile entsteht neu (darum zuerst heraus — sie hatte den Fokus, der soll an denselben Knopf).
      zeileEl?.remove();
      // Erst die Meldung, dann neu zeichnen: ohne Knopf in der Liste fällt der Fokus auf sie.
      melde(erfolgText(aktion, g?.id ?? null, g?.name ?? "", neu, art), "ok");
      neuZeichnen({ id: g?.id ?? null, aktion: aktion === "waehlen" || aktion === "automatik" ? null : aktion });
      o.nachher();
    } catch (err) {
      laeuft = false;
      const status = err instanceof GeraetAktionFehler ? err.status : null;
      const code = err instanceof GeraetAktionFehler ? err.code : null;
      const text = aktionFehlerText(status, code, g?.name ?? `Der ${geraetWort(art)}`, art);
      for (const k of gesperrt) k.disabled = false;
      b.removeAttribute("aria-busy");
      if (!g || status === 404 || status === 401) {
        // Die Zeile gibt es vielleicht nicht mehr — die Meldung steht unter der Liste, der Stand kommt neu.
        if (g) schliessen(g.id, aktion);
        melde(text, "fehler");
        o.nachher();
        return;
      }
      zeilenFehler = { id: g.id, text, um: Date.now() };
      if (aktion === "trennen") {
        // Die Rückfrage ist vorbei: wieder „Trennen“, die Zeile bleibt (ihre Meldezeile sagt den Fehler an).
        offen = null;
        b.textContent = AKTION_WORT.trennen;
        delete b.dataset.sicher;
        zeileEl?.querySelector('button[data-aktion="abbrechen"]')?.remove();
        for (const k of b.parentElement?.querySelectorAll<HTMLElement>("button") ?? []) k.hidden = false;
      }
      if (aktion === "waehlen") offen = null;
      zeilenMeldung(zeileEl, text, "fehler");
      if (feld) {
        // Das Feld bleibt offen: Martin kann den Namen gleich ändern.
        feld.setAttribute("aria-invalid", "true");
        feld.focus();
      } else {
        b.focus();
      }
    }
  };

  zurueck.addEventListener("click", () => void ausfuehren("automatik", null, zurueck));

  return {
    element,
    meldung,
    zeichne: (roh, abgefragtUm = performance.now()) => {
      if (abgefragtUm < aktionFertigUm) return;
      const s = inNfc(roh);
      stand = s;
      update?.stand(s.updateStarten);
      if (laeuft) return;
      if (signatur(s) === gezeichnet) return;
      // Der Stand hat sich geändert: eine ältere Meldung könnte nicht mehr stimmen.
      if (meldung.textContent && Date.now() - meldungUm > MELDUNG_GILT_MS) {
        meldung.textContent = "";
        delete meldung.dataset.art;
      }
      if (zeilenFehler && Date.now() - zeilenFehler.um > MELDUNG_GILT_MS) zeilenFehler = null;
      neuZeichnen();
    },
  };
}

/** Was die Liste zeichnet — gleich = nichts neu bauen (der 10-s-Abruf soll nichts flackern lassen). */
function signatur(s: ListenStand): string {
  return JSON.stringify([s.geraete, s.wahl, s.aktuell ?? null, !!s.download, s.downloadVersion ?? null, s.updateStarten?.erlaubt ?? null]);
}
